import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  makeStyles
} from "@material-ui/core";
import InternalTicketDialog from "../InternalTicketDialog";
import {
  formatInternalTicketProtocol,
  getInternalTicketPriorityLabel,
  getInternalTicketSlaLabel,
  getInternalTicketStatusLabel,
  formatInternalTicketCreation
} from "../../helpers/internalTicket";

const useStyles = makeStyles(theme => ({
  wrapper: {
    padding: theme.spacing(1.25, 1.75),
    borderBottom: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(148, 163, 184, 0.12)"
        : "rgba(148, 163, 184, 0.16)"
    }`,
    background: theme.palette.type === "dark"
      ? "rgba(15, 23, 42, 0.12)"
      : "rgba(248, 250, 252, 0.58)"
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1)
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  title: {
    fontWeight: 700
  },
  countChip: {
    height: 22,
    fontSize: "0.72rem",
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(148, 163, 184, 0.10)"
      : "rgba(148, 163, 184, 0.12)"
  },
  actionButton: {
    minWidth: 0,
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25)
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.85)
  },
  row: {
    padding: theme.spacing(1, 1.25),
    borderRadius: 12,
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1.25),
    transition: "border-color 0.18s ease, background-color 0.18s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(15, 118, 110, 0.10)"
        : "rgba(15, 118, 110, 0.04)"
    }
  },
  rowMain: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minWidth: 0
  },
  protocol: {
    fontWeight: 700,
    color: theme.palette.primary.main,
    flex: "none",
    fontSize: "0.76rem"
  },
  titleText: {
    fontWeight: 700,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  subtitleText: {
    color: theme.palette.text.secondary,
    fontSize: "0.84rem",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.65),
    justifyContent: "flex-end"
  },
  metaChip: {
    height: 22,
    fontSize: "0.7rem"
  },
  empty: {
    color: theme.palette.text.secondary
  }
}));

const InternalTicketsPanel = ({
  ticketId,
  internalTickets,
  onRefresh,
  embedded = true
}) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const openCreateDialog = () => {
    setSelectedTicket(null);
    setDialogOpen(true);
  };

  const openEditDialog = ticket => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const orderedTickets = useMemo(
    () => (internalTickets || []).slice().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    [internalTickets]
  );

  return (
    <div className={embedded ? classes.wrapper : undefined}>
      <div className={classes.header}>
        <div className={classes.titleRow}>
          <Typography className={classes.title}>
            Tickets internos
          </Typography>
          <Chip
            size="small"
            label={`${orderedTickets.length} vinculados`}
            className={classes.countChip}
          />
        </div>
        <Button
          variant="outlined"
          color="primary"
          size="small"
          className={classes.actionButton}
          onClick={openCreateDialog}
        >
          Novo ticket interno
        </Button>
      </div>

      {orderedTickets.length ? (
        <div className={classes.list}>
          {orderedTickets.map(internalTicket => (
            <Paper
              variant="outlined"
              key={internalTicket.id}
              className={classes.row}
              onClick={() => openEditDialog(internalTicket)}
            >
              <div className={classes.rowMain}>
                <div className={classes.rowTop}>
                  <Typography variant="body2" className={classes.protocol}>
                    {formatInternalTicketProtocol(internalTicket)}
                  </Typography>
                  <Typography className={classes.titleText}>
                    {internalTicket.title}
                  </Typography>
                </div>
                <Typography className={classes.subtitleText}>
                  {internalTicket.assignedUser?.name || "Sem responsável"} • {internalTicket.description || "Sem descrição adicional."}
                </Typography>
                <Typography className={classes.subtitleText}>
                  {formatInternalTicketCreation(internalTicket, "internalTickets.page.card")}
                </Typography>
              </div>
              <div className={classes.metaRow}>
                <Chip
                  size="small"
                  label={getInternalTicketStatusLabel(internalTicket.status)}
                  className={classes.metaChip}
                />
                <Chip
                  size="small"
                  label={getInternalTicketPriorityLabel(internalTicket.priority)}
                  className={classes.metaChip}
                />
                <Chip
                  size="small"
                  label={getInternalTicketSlaLabel(internalTicket.slaStatus)}
                  className={classes.metaChip}
                />
              </div>
            </Paper>
          ))}
        </div>
      ) : (
        <Typography variant="body2" className={classes.empty}>
          Nenhum ticket interno vinculado a este atendimento.
        </Typography>
      )}

      <InternalTicketDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ticketId={ticketId}
        internalTicket={selectedTicket}
        onSaved={onRefresh}
      />
    </div>
  );
};

export default InternalTicketsPanel;
